
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Languages } from "lucide-react";
import { AIHybridService } from "@/utils/aiHybridService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuestionTranslationButtonProps {
  questionText: string;
  options: string[];
  onTranslation: (translatedQuestion: string, translatedOptions: string[]) => void;
  isTranslationVisible?: boolean;
  onToggleTranslation?: () => void;
}

const QuestionTranslationButton: React.FC<QuestionTranslationButtonProps> = ({
  questionText,
  options,
  onTranslation,
  isTranslationVisible,
  onToggleTranslation
}) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("hindi");

  const handleTranslate = async () => {
    // If translation is already available and toggle function is provided, use it
    if (isTranslationVisible !== undefined && onToggleTranslation) {
      // If we're showing translation and user clicks again, just toggle it off
      if (isTranslationVisible) {
        onToggleTranslation();
        return;
      }
    }

    try {
      setIsTranslating(true);
      
      // Format options as a single string with clear delimiters for translation
      const optionsText = options.map((option, index) => 
        `${String.fromCharCode(65 + index)}: ${option}`
      ).join('|');
      
      const result = await AIHybridService.translateQuizContent(
        questionText,
        options,
        selectedLanguage
      );
      
      if (!result) {
        throw new Error("Translation failed");
      }
      
      onTranslation(result.translatedQuestion, result.translatedOptions);

      // If toggle function provided, call it to show translation
      if (onToggleTranslation && !isTranslationVisible) {
        onToggleTranslation();
      }

      toast.success(`Translated to ${selectedLanguage}`, {
        description: "Translation complete"
      });
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Failed to translate content", {
        description: "Please try again or select a different language"
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const buttonText = isTranslating 
    ? "Translating..." 
    : isTranslationVisible 
      ? "Hide Translation" 
      : "Translate";

  return (
    <div className="flex items-center gap-2">
      <Select
        value={selectedLanguage}
        onValueChange={setSelectedLanguage}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Select language" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="hindi">Hindi</SelectItem>
          <SelectItem value="hinglish">Hinglish</SelectItem>
          <SelectItem value="english">English</SelectItem>
        </SelectContent>
      </Select>
      
      <Button 
        variant="outline"
        onClick={handleTranslate}
        disabled={isTranslating}
        className={`flex items-center gap-2 transition-all duration-300 ${
          isTranslationVisible ? "bg-blue-100 text-blue-700 border-blue-300" : ""
        }`}
      >
        {isTranslating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Languages className="w-4 h-4" />
        )}
        {buttonText}
      </Button>
    </div>
  );
};

export default QuestionTranslationButton;
